/**
 * シーン音声 (動画クリップの埋め込み音声・現場音) の**区間ごとの音量** (区間ミュート / 減音)。
 *
 * `TimelineItem.audioRegions: AudioRegion[]`。秒は**素材秒** (波形・IN / OUT と同じ軸) で持つ
 * (速度を変えても「この音を消す」の意味が変わらないように。テロップ / オーバーレイの「表示秒」とは違う)。
 * `volume` は 0〜1 のリニア値で、0 = ミュート。評価はシーン全体の音量 (item.volume) ×
 * フェード (audioFadeInSec / audioFadeOutSec) × 区間のゲインの掛け算。
 *
 * 区間の両端は AUDIO_REGION_RAMP_SEC (50 ms) の直線で繋ぐ (区間の**内側**に取る。瞬時に切ると
 * クリック音が出るため)。短い区間ではランプを区間の半分までに縮める。
 *
 * このファイルは zod も React も持ち込まない純関数 (プレビュー・MP4 レンダラー・Premiere / FCPXML
 * 書き出し・試聴プレイヤーが同じ式を使う)。**正本はこのファイル**で、
 * `video-editor/src/lib/audioRegions.ts` (レンダラー) と
 * `backend/services/video_edit/audio_regions.py` (保存時検証・AI 編集の分割 / 区間変更・引き継ぎ) が
 * 手動コピー。3 つに同じケースのテストを置いて一致を担保する。
 * 素材秒 → 表示秒の写像 (itemDisplaySecAtSource) は schema/project.ts が担い、ここには持ち込まない
 * (循環 import を避ける)。
 */
import { fadeGainAt } from './audioCue';

/** 保存形。 */
export interface AudioRegion {
  id: string;
  /** 区間の開始 (素材秒)。 */
  startSec: number;
  /** 区間の終わり (素材秒。開始より後)。 */
  endSec: number;
  /** 区間内の音量 (0〜1。0 = ミュート)。 */
  volume: number;
}

/** 表示秒に写した区間 (評価用)。 */
export interface AudioGainSpan {
  startSec: number;
  endSec: number;
  volume: number;
}

/** 折れ線 (エンベロープ) の 1 点。 */
export interface EnvelopePoint {
  sec: number;
  value: number;
}

/** 区間の両端のランプ (秒)。表示秒で固定 (速度を変えてもクリック対策の長さは変えない)。 */
export const AUDIO_REGION_RAMP_SEC = 0.05;
/** UI で作れる区間の最短 (素材秒)。 */
export const AUDIO_REGION_MIN_SEC = 0.1;
/** 1 シーンあたりの区間の上限。 */
export const AUDIO_REGIONS_MAX = 50;
/** 新しく置く区間の既定の音量 (ミュート)。 */
export const AUDIO_REGION_DEFAULT_VOLUME = 0;
/** 新しく置く区間の既定の長さ (素材秒)。 */
export const AUDIO_REGION_DEFAULT_SEC = 1;

const EPS = 1e-6;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** half-up の丸め (timedSpan.ts と同じ規則。Math.round は負値で half-down になる)。 */
function roundHalfUp(value: number, digits: number): number {
  const factor = 10 ** digits;
  const scaled = value * factor;
  const rounded = scaled >= 0 ? Math.floor(scaled + 0.5) : -Math.floor(-scaled + 0.5);
  return rounded === 0 ? 0 : rounded / factor;
}

/** 秒の丸め (保存形は小数第 2 位まで)。 */
function roundSec(value: number): number {
  return roundHalfUp(value, 2);
}

function defaultRegionId(index: number): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `ar-${Date.now().toString(36)}-${index}-${Math.random().toString(36).slice(2, 8)}`;
}

export interface NormalizeAudioRegionsOptions {
  /** 区間をこの素材区間に切り詰める (シーンの IN / OUT)。省略で切り詰めない。 */
  inSec?: number;
  outSec?: number;
  /** id の無い要素に振る id。 */
  makeId?: (index: number) => string;
}

/**
 * 外部 (保存 JSON / AI / UI の下書き) からの入力を保存形へ正規化する。
 * - 配列でなければ `undefined` (区間なし)
 * - 数値でない / 開始 ≧ 終わり / 音量 1 (= 効かない) の要素は落とす。音量は 0〜1 にクランプし
 *   小数第 2 位へ、秒も小数第 2 位へ丸める (half-up)
 * - `inSec` / `outSec` を渡すとその区間に切り詰め、空になった要素は落とす
 * - 開始順に並べ、重なりは**前の区間を優先**して後ろの区間の開始を詰める。隣り合って同じ音量なら 1 つにまとめる
 * - 上限 AUDIO_REGIONS_MAX を超えた分は落とす
 * 空になったら `undefined` (キーごと落とす = 保存形は「区間なし = キー無し」の 1 通り)。
 */
export function normalizeAudioRegions(
  raw: unknown,
  options: NormalizeAudioRegionsOptions = {},
): AudioRegion[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const makeId = options.makeId ?? defaultRegionId;
  const lower = isFiniteNumber(options.inSec) ? options.inSec : -Infinity;
  const upper = isFiniteNumber(options.outSec) ? options.outSec : Infinity;
  const cleaned: AudioRegion[] = [];
  raw.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
    const record = entry as Record<string, unknown>;
    if (!isFiniteNumber(record.startSec) || !isFiniteNumber(record.endSec)) return;
    // volume 省略 = ミュート (0)。不正値も 0 に倒す
    const rawVolume = isFiniteNumber(record.volume) ? record.volume : AUDIO_REGION_DEFAULT_VOLUME;
    const volume = roundHalfUp(Math.min(1, Math.max(0, rawVolume)), 2);
    if (volume >= 1) return;
    const start = roundSec(Math.max(0, Math.max(record.startSec, lower)));
    const end = roundSec(Math.min(record.endSec, upper));
    if (end - start <= EPS) return;
    const id = typeof record.id === 'string' && record.id.length > 0 ? record.id : makeId(index);
    cleaned.push({ id, startSec: start, endSec: end, volume });
  });
  cleaned.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);
  const merged: AudioRegion[] = [];
  for (const region of cleaned) {
    const prev = merged[merged.length - 1];
    if (!prev) {
      merged.push(region);
      continue;
    }
    if (region.startSec < prev.endSec - EPS) {
      // 重なり: 前の区間を優先し、後ろの開始を詰める
      if (region.endSec - prev.endSec <= EPS) continue;
      if (Math.abs(region.volume - prev.volume) <= EPS) {
        prev.endSec = region.endSec;
        continue;
      }
      merged.push({ ...region, startSec: prev.endSec });
      continue;
    }
    if (region.startSec - prev.endSec <= EPS && Math.abs(region.volume - prev.volume) <= EPS) {
      // 隣接して同じ音量 → 1 つに
      prev.endSec = region.endSec;
      continue;
    }
    merged.push(region);
  }
  const limited = merged.slice(0, AUDIO_REGIONS_MAX);
  return limited.length > 0 ? limited : undefined;
}

/** シーンの IN / OUT が変わったときに区間を新しい素材区間へ切り詰める (normalize の別名)。 */
export function clampAudioRegionsToRange(
  regions: readonly AudioRegion[] | undefined,
  inSec: number,
  outSec: number,
): AudioRegion[] | undefined {
  return normalizeAudioRegions(regions, { inSec, outSec });
}

/** 区間の配列が (id を含めて) 同じか。 */
export function audioRegionsEqual(
  a: readonly AudioRegion[] | undefined,
  b: readonly AudioRegion[] | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((region, index) => {
    const other = b[index]!;
    return (
      region.id === other.id &&
      Math.abs(region.startSec - other.startSec) <= EPS &&
      Math.abs(region.endSec - other.endSec) <= EPS &&
      Math.abs(region.volume - other.volume) <= EPS
    );
  });
}

/**
 * シーンを素材秒 `sourceSplit` で 2 つに分けたときの (前半, 後半) の区間 (分割 splitItem の規則)。
 * 境界をまたぐ区間は両側に切り分け、後半側は新しい id。素材秒なので位置の付け替えは要らない。
 */
export function splitAudioRegionsAtSource(
  regions: readonly AudioRegion[] | undefined,
  sourceSplit: number,
  makeId: (index: number) => string,
): { first: AudioRegion[] | undefined; second: AudioRegion[] | undefined } {
  const first: AudioRegion[] = [];
  const second: AudioRegion[] = [];
  let created = 0;
  for (const region of regions ?? []) {
    if (region.endSec <= sourceSplit + EPS) {
      first.push(region);
    } else if (region.startSec >= sourceSplit - EPS) {
      second.push(region);
    } else {
      first.push({ ...region, endSec: roundSec(sourceSplit) });
      second.push({ ...region, id: makeId(created++), startSec: roundSec(sourceSplit) });
    }
  }
  return { first: first.length > 0 ? first : undefined, second: second.length > 0 ? second : undefined };
}

/** 2 シーンの結合 (mergeItems) で区間をつなぐ。素材上で続いているので並べて正規化するだけ。 */
export function mergeAudioRegions(
  first: readonly AudioRegion[] | undefined,
  second: readonly AudioRegion[] | undefined,
): AudioRegion[] | undefined {
  return normalizeAudioRegions([...(first ?? []), ...(second ?? [])]);
}

/** 1 区間のゲイン (表示秒 t)。区間の外は 1、内側の両端を rampSec の直線で繋ぐ。 */
export function audioGainSpanAt(span: AudioGainSpan, tSec: number, rampSec = AUDIO_REGION_RAMP_SEC): number {
  const { startSec, endSec, volume } = span;
  if (tSec < startSec || tSec >= endSec) return 1;
  const ramp = Math.max(0, Math.min(rampSec, (endSec - startSec) / 2));
  if (ramp <= 0) return volume;
  if (tSec < startSec + ramp) {
    const u = (tSec - startSec) / ramp;
    return 1 + (volume - 1) * u;
  }
  if (tSec > endSec - ramp) {
    const u = (endSec - tSec) / ramp;
    return 1 + (volume - 1) * u;
  }
  return volume;
}

/** 複数区間のゲイン (表示秒 t)。重ならない前提だが、重なれば小さい方。 */
export function audioRegionGainAt(
  spans: readonly AudioGainSpan[],
  tSec: number,
  rampSec = AUDIO_REGION_RAMP_SEC,
): number {
  let gain = 1;
  for (const span of spans) gain = Math.min(gain, audioGainSpanAt(span, tSec, rampSec));
  return Math.min(1, Math.max(0, gain));
}

/** 区間のゲインが折れる時刻 (表示秒・昇順・重複なし)。 */
export function audioRegionBreakpoints(
  spans: readonly AudioGainSpan[],
  rampSec = AUDIO_REGION_RAMP_SEC,
): number[] {
  const points = new Set<number>();
  for (const span of spans) {
    const ramp = Math.max(0, Math.min(rampSec, (span.endSec - span.startSec) / 2));
    points.add(span.startSec);
    points.add(span.endSec);
    if (ramp > 0) {
      points.add(span.startSec + ramp);
      points.add(span.endSec - ramp);
    }
  }
  return [...points].sort((a, b) => a - b);
}

/**
 * 折れ線の値 (sec)。点の外側は端の値で保持し、点の間は直線補間。点が無ければ 1。
 * 試聴プレイヤー (GainNode の自動化) と書き出しの間引き判定で使う。
 */
export function envelopeValueAt(points: readonly EnvelopePoint[], sec: number): number {
  if (points.length === 0) return 1;
  if (sec <= points[0]!.sec) return points[0]!.value;
  const last = points[points.length - 1]!;
  if (sec >= last.sec) return last.value;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1]!;
    const next = points[i]!;
    if (sec <= next.sec) {
      const span = next.sec - prev.sec;
      if (span <= 0) return next.value;
      return prev.value + ((next.value - prev.value) * (sec - prev.sec)) / span;
    }
  }
  return last.value;
}

/**
 * 区間だけのエンベロープ (表示秒 → ゲイン 0〜1)。両端 (0 / spanSec) を含む折れ点を返す。
 * 区間が無ければ空配列。
 */
export function audioRegionEnvelope(
  spans: readonly AudioGainSpan[],
  spanSec: number,
  rampSec = AUDIO_REGION_RAMP_SEC,
): EnvelopePoint[] {
  if (spans.length === 0 || !(spanSec > 0)) return [];
  const secs = new Set<number>([0, spanSec]);
  for (const sec of audioRegionBreakpoints(spans, rampSec)) {
    if (sec > 0 && sec < spanSec) secs.add(sec);
  }
  const sorted = [...secs].sort((a, b) => a - b);
  // 折れ点ちょうどの値は半開区間の定義どおり (startSec = 1 / startSec+ramp = volume /
  // endSec-ramp = volume / endSec = 1) なので、そのまま評価すれば直線補間で元の形が再現できる
  return sorted.map((sec) => ({ sec, value: audioRegionGainAt(spans, sec, rampSec) }));
}

/** 区間の音量の表示 (ミュート / n%)。 */
export function audioRegionVolumeLabel(volume: number): string {
  if (volume <= 0) return 'ミュート';
  return `${Math.round(volume * 100)}%`;
}

/** 要約の 1 行 (「2か所: ミュート 1・音量50% 1」)。区間が無ければ空文字列。 */
export function describeAudioRegions(regions: readonly AudioRegion[] | undefined): string {
  if (!regions || regions.length === 0) return '';
  const muted = regions.filter((r) => r.volume <= 0).length;
  const lowered = regions.length - muted;
  const parts: string[] = [];
  if (muted > 0) parts.push(`ミュート${muted}`);
  if (lowered > 0) parts.push(`減音${lowered}`);
  return `${regions.length}か所 (${parts.join('・')})`;
}

/**
 * シーン音声 1 本の音量エンベロープ (書き出し用。表示秒 → 実効音量 0〜1)。
 * シーン全体の音量 × フェード (audioFadeInSec / audioFadeOutSec) × 区間のゲインを、折れる時刻だけで
 * 折れ線にする (Premiere の Audio Levels キーフレーム / Final Cut の keyframeAnimation)。
 * フェードの折れ点と区間の折れ点の間でゲインの積は厳密には直線にならないが、ランプは 50 ms なので
 * 直線近似で足りる (ダッキングと同じ方針)。
 * - 区間が無くフェードだけなら、従来の fadeLevelKeyframes と同じ点 (両端 0 / spanSec は含めない)
 * - 区間があれば両端も含める (区間の外で基準音量を保つため)
 * - どちらも無ければ空配列 (音量 1 本の値だけで足りる)
 */
export function sceneAudioEnvelope(params: {
  volume: number;
  spanSec: number;
  fadeInSec?: number;
  fadeOutSec?: number;
  spans: readonly AudioGainSpan[];
  rampSec?: number;
}): EnvelopePoint[] {
  const { volume, spanSec, fadeInSec, fadeOutSec, spans } = params;
  const rampSec = params.rampSec ?? AUDIO_REGION_RAMP_SEC;
  if (!(spanSec > 0)) return [];
  const hasFade = (fadeInSec ?? 0) > 0 || (fadeOutSec ?? 0) > 0;
  const hasRegions = spans.length > 0;
  if (!hasFade && !hasRegions) return [];
  const secs = new Set<number>();
  if (hasRegions) {
    secs.add(0);
    secs.add(spanSec);
    for (const sec of audioRegionBreakpoints(spans, rampSec)) {
      if (sec > 0 && sec < spanSec) secs.add(sec);
    }
  }
  if (hasFade) {
    // fadeGainAt の折れ点 (比例縮小後のフェード秒) を fadeGainAt 自身から逆算せず、十分細かく境界を拾う:
    // 0 と spanSec、フェードの終わり / 始まりの位置。縮小は fadeGainAt の中で掛かるので、
    // ここでは両端に加えて「フェードの境目」を候補に入れ、値は fadeGainAt で評価する
    const fi = Math.max(0, fadeInSec ?? 0);
    const fo = Math.max(0, fadeOutSec ?? 0);
    const total = fi + fo;
    const scale = total > spanSec && total > 0 ? spanSec / total : 1;
    if (fi > 0) {
      secs.add(0);
      secs.add(Math.min(spanSec, fi * scale));
    }
    if (fo > 0) {
      secs.add(Math.max(0, spanSec - fo * scale));
      secs.add(spanSec);
    }
  }
  const sorted = [...secs].sort((a, b) => a - b);
  return sorted.map((sec) => ({
    sec,
    value:
      Math.min(1, Math.max(0, volume)) *
      (hasFade ? fadeGainAt(sec, spanSec, fadeInSec, fadeOutSec) : 1) *
      (hasRegions ? audioRegionGainAt(spans, sec, rampSec) : 1),
  }));
}
